import { IDataTableColumn, useGridState, useSearchApi, FieldSet } from "nsitools-react";
import * as React from "react";

import { KennelOwnerApi, KennelOwnerDto, KennelOwnerSearch } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { AddButton, CustomDataTable, EditButton, ExportButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { Link } from "react-router-dom";
import { Dialog, Classes, Colors, Intent, ButtonGroup } from "@blueprintjs/core";
import { KennelOwnersPanelStack } from "./kennelOwnersPanelStack/KennelOwnersPanelStack";

export interface IKennelOwnersPageProps {
  kennelId: number;
}

export const KennelOwnersPage: React.FunctionComponent<IKennelOwnersPageProps> = ({ kennelId }) => {
  const { t } = useTl();
  const api = useApiService(KennelOwnerApi);
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();
  const [kennelOwnershipId, setkennelOwnershipId] = React.useState<number>(0);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { name: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: KennelOwnerSearch) => {
      const sobj = KennelOwnerSearch.fromJS({
        ...sObj,
        kennelId: kennelId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, currentLanguage.languageId, kennelId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  //IF GOD MODE
  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "action",
        autoFitContent: true,
        alignment: "center",
        render: (row: KennelOwnerDto) => (
          <EditButton
            onClick={(e) => {
              e.stopPropagation();
              setkennelOwnershipId(row.kennelOwnershipId);
              addItemFunction();
            }}
          />
        ),
      },
      {
        header: () => t(ETLCodes.Owner),
        fieldName: "name",
        render: (row: KennelOwnerDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.name}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.OwnerFrom),
        fieldName: "ownerFrom",
      },
      {
        header: () => t(ETLCodes.OwnerTo),
        fieldName: "ownerTo",
      },
    ],
    [addItemFunction, t]
  );

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Owners)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            <AddButton
              onClick={(e) => {
                e.stopPropagation();
                addItemFunction();
              }}
              text={t(ETLCodes.General_Add)}
              intent={Intent.PRIMARY}
            />
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.OwnerSelection)}
        isOpen={dialogOpened}
        onClose={() => {
          setDialogOpened(false);
          setkennelOwnershipId(0);
        }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1000, height: 600, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <KennelOwnersPanelStack
            onSaved={() => {
              setDialogOpened(false);
              search();
              setkennelOwnershipId(null);
            }}
            kennelId={kennelId}
            kennelOwnershipId={kennelOwnershipId}
          />
        </div>
      </Dialog>
    </>
  );
};
